// Profile panel, opened from the identity pill in the lobby header. Its only
// content today is the account's self-bot model: upload one, see what the
// server made of it, remove it again.
//
// The model is account state, not room state, so it is read from
// /api/profile/model on open rather than carried in the lobby payload. Guests
// never get here -- the pill is inert for them -- but every response is still
// treated as authoritative: a 401 rerenders as "signed out" instead of
// pretending the upload worked.
import { authState, net } from '../engine/state.js';

const MODEL_ENDPOINT = '/api/profile/model';
const ACCOUNT_ENDPOINT = '/api/profile/account';
// Mirrors the server ceiling so an oversized pick fails instantly instead of
// after the browser has pushed 40 MB up the wire. The server still decides.
const FALLBACK_MAX_BYTES = 16 * 1024 * 1024;

const els = {};
let maxBytes = FALLBACK_MAX_BYTES;
let busy = false;

function cache() {
  if (els.cached) return els;
  els.modal = document.getElementById('profile-modal');
  els.openBtn = document.getElementById('lobby-identity-pill');
  // The rail's Bot Lab item: the profile IS where a bot model lives.
  els.botLabBtn = document.getElementById('lobby-nav-botlab-btn');
  els.closeBtn = document.getElementById('profileCloseBtn');
  els.accountName = document.getElementById('profileAccountName');
  els.empty = document.getElementById('profileModelEmpty');
  els.meta = document.getElementById('profileModelMeta');
  els.name = document.getElementById('profileModelName');
  els.size = document.getElementById('profileModelSize');
  els.graph = document.getElementById('profileModelGraph');
  els.inputs = document.getElementById('profileModelInputs');
  els.outputs = document.getElementById('profileModelOutputs');
  els.speed = document.getElementById('profileModelSpeed');
  els.uploaded = document.getElementById('profileModelUploaded');
  els.record = document.getElementById('profileModelRecord');
  els.stale = document.getElementById('profileModelStale');
  els.fileInput = document.getElementById('profileModelFile');
  els.uploadBtn = document.getElementById('profileUploadBtn');
  els.uploadLabel = document.getElementById('profileUploadLabel');
  els.removeBtn = document.getElementById('profileRemoveBtn');
  els.status = document.getElementById('profileStatus');
  els.deleteName = document.getElementById('profileDeleteName');
  els.deleteConfirm = document.getElementById('profileDeleteConfirm');
  els.deleteBtn = document.getElementById('profileDeleteBtn');
  els.deleteStatus = document.getElementById('profileDeleteStatus');
  els.cached = true;
  return els;
}

function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
}

function formatWhen(timestamp) {
  const ms = Number(timestamp) || 0;
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString();
  } catch (_) {
    return new Date(ms).toISOString();
  }
}

function formatNames(names) {
  return Array.isArray(names) && names.length ? names.join(', ') : '-';
}

function setStatusText(text, kind = '') {
  if (!els.status) return;
  els.status.textContent = text || '';
  els.status.classList.toggle('is-error', kind === 'error');
  els.status.classList.toggle('is-ok', kind === 'ok');
}

function setDeleteStatusText(text, kind = '') {
  if (!els.deleteStatus) return;
  els.deleteStatus.textContent = text || '';
  els.deleteStatus.classList.toggle('is-error', kind === 'error');
  els.deleteStatus.classList.toggle('is-ok', kind === 'ok');
}

// The delete button is a pure function of what is currently typed, never of the
// last thing that happened to it. setBusy routes through here for that reason:
// a finished upload must not re-enable a delete whose confirm field is empty.
function syncDeleteButton() {
  if (!els.deleteBtn) return;
  const typed = (els.deleteConfirm?.value || '').trim().toLowerCase();
  const expected = String(net.playerName || '').trim().toLowerCase();
  els.deleteBtn.disabled = busy || !expected || typed !== expected;
}

function setBusy(next) {
  busy = Boolean(next);
  if (els.uploadBtn) els.uploadBtn.disabled = busy;
  if (els.removeBtn) els.removeBtn.disabled = busy;
  syncDeleteButton();
}

// "How is my bot doing?" in one line. Deliberately plain counts and averages
// rather than a verdict: a handful of live matches against whoever happened to
// be in the room cannot support "better" or "worse", and pretending otherwise
// would be worse than saying nothing.
function describeRecord(stats) {
  if (!stats) return 'not played yet';
  if (!stats.matches) return 'not played yet - hand your slot to the bot to try it';

  const plural = stats.matches === 1 ? 'match' : 'matches';
  const parts = [`${stats.matches} ${plural}`, `avg score ${stats.avgScore}`, `best ${stats.bestScore}`];
  if (stats.aliveShare !== null && stats.aliveShare !== undefined) {
    parts.push(`alive ${Math.round(stats.aliveShare * 100)}% of the time`);
  }
  let text = parts.join(' · ');
  if (stats.previous?.matches) {
    // The comparison a player actually wants after replacing a model, stated as
    // two numbers rather than a winner.
    text += ` (earlier models: ${stats.previous.matches} played, avg ${stats.previous.avgScore})`;
  }
  return text;
}

function renderModel(model, stats) {
  const attached = Boolean(model);
  if (els.empty) els.empty.hidden = attached;
  if (!attached && els.stale) els.stale.hidden = true;
  if (els.meta) els.meta.hidden = !attached;
  if (els.removeBtn) els.removeBtn.hidden = !attached;
  if (els.uploadLabel) els.uploadLabel.textContent = attached ? 'Replace model' : 'Upload model';
  if (!attached) return;

  // textContent throughout: filenames and graph names come from an uploaded
  // file, so none of this is trusted markup.
  if (els.name) els.name.textContent = model.filename || 'model.onnx';
  if (els.size) els.size.textContent = formatBytes(model.byteSize);
  if (els.graph) {
    const graphName = model.graphName || '(unnamed)';
    const producer = model.producer ? ` - ${model.producer}` : '';
    els.graph.textContent = `${graphName}${producer} - ${model.nodeCount || 0} ops`;
  }
  if (els.inputs) els.inputs.textContent = formatNames(model.inputs);
  if (els.outputs) els.outputs.textContent = formatNames(model.outputs);
  if (els.speed) {
    // Measured on the server at upload, which is the only place the number
    // means anything: it is what decides whether a match can carry the model.
    els.speed.textContent = model.meanInferenceMs > 0
      ? `${model.meanInferenceMs.toFixed(2)} ms per decision (${model.worstInferenceMs.toFixed(2)} ms worst)`
      : '-';
  }
  if (els.uploaded) els.uploaded.textContent = formatWhen(model.uploadedAt);
  if (els.record) els.record.textContent = describeRecord(stats);
  // A model stored before the current observation format cannot be run, but is
  // not deleted either -- so say plainly why it is sitting idle.
  if (els.stale) els.stale.hidden = model.runnable !== false;
}

async function readPayload(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(typeof data?.error === 'string' ? data.error : `Request failed (${response.status})`);
  }
  return data;
}

function applyPayload(payload) {
  if (Number.isFinite(payload?.maxBytes) && payload.maxBytes > 0) {
    maxBytes = payload.maxBytes;
  }
  renderModel(payload?.model || null, payload?.stats || null);
}

async function refresh() {
  cache();
  setBusy(true);
  try {
    const response = await fetch(MODEL_ENDPOINT, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store'
    });
    applyPayload(await readPayload(response));
    setStatusText('');
  } catch (error) {
    renderModel(null);
    setStatusText(error?.message || 'Could not load your model.', 'error');
  } finally {
    setBusy(false);
  }
}

async function uploadFile(file) {
  if (!file) return;
  if (!/\.onnx$/i.test(file.name || '')) {
    setStatusText('Pick a .onnx file.', 'error');
    return;
  }
  if (file.size > maxBytes) {
    setStatusText(`That file is ${formatBytes(file.size)}; the limit is ${formatBytes(maxBytes)}.`, 'error');
    return;
  }

  setBusy(true);
  setStatusText(`Uploading ${file.name}...`);
  try {
    // Raw bytes rather than base64 or multipart: the server stores the file
    // verbatim and checks its hash, and nothing else rides along except the
    // name, which goes in a header (encoded, since headers are ASCII).
    const response = await fetch(MODEL_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Model-Filename': encodeURIComponent(file.name || 'model.onnx')
      },
      body: await file.arrayBuffer()
    });
    applyPayload(await readPayload(response));
    setStatusText('Model saved.', 'ok');
  } catch (error) {
    setStatusText(error?.message || 'Upload failed.', 'error');
  } finally {
    setBusy(false);
  }
}

async function removeModel() {
  setBusy(true);
  setStatusText('Removing model...');
  try {
    const response = await fetch(MODEL_ENDPOINT, {
      method: 'DELETE',
      credentials: 'include'
    });
    applyPayload(await readPayload(response));
    setStatusText('Model removed.', 'ok');
  } catch (error) {
    setStatusText(error?.message || 'Could not remove the model.', 'error');
  } finally {
    setBusy(false);
  }
}

async function deleteAccount() {
  setBusy(true);
  setDeleteStatusText('Deleting your account...');
  try {
    const response = await fetch(ACCOUNT_ENDPOINT, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      // The server compares this against the session's own username. Sending
      // what was typed rather than what is stored would let a mismatch through
      // on the client and be rejected server-side anyway; sending the account
      // name keeps the check where it belongs, on the server.
      body: JSON.stringify({ username: String(net.playerName || '').trim() })
    });
    await readPayload(response);
    // Deliberately no setBusy(false): the controls stay dead until the reload,
    // so a second click cannot fire against an account that is already gone.
    setDeleteStatusText('Account deleted. Returning to sign-in...', 'ok');
    // The cookie is cleared server-side, so a reload lands on the login screen
    // with no session -- and drops the game socket rather than leaving it open
    // under an account that no longer exists.
    window.setTimeout(() => window.location.reload(), 900);
  } catch (error) {
    setDeleteStatusText(error?.message || 'Could not delete your account.', 'error');
    setBusy(false);
  }
}

function openProfile() {
  cache();
  if (!els.modal) return;
  // Guests have no account to hang a model on; for them the pill and Bot Lab
  // open the sign-in modal instead (network/auth.js), so this ignores them.
  if (!authState.authenticated || authState.guest) return;
  if (els.accountName) els.accountName.textContent = net.playerName || 'Player';
  if (els.deleteName) els.deleteName.textContent = net.playerName || 'your username';
  // Never reopen with the confirm field still armed from last time.
  if (els.deleteConfirm) els.deleteConfirm.value = '';
  setDeleteStatusText('');
  syncDeleteButton();
  els.modal.classList.remove('hidden');
  els.modal.setAttribute('aria-hidden', 'false');
  setStatusText('');
  renderModel(null);
  refresh();
}

function closeProfile() {
  cache();
  if (!els.modal) return;
  els.modal.classList.add('hidden');
  els.modal.setAttribute('aria-hidden', 'true');
  if (els.fileInput) els.fileInput.value = '';
  if (els.deleteConfirm) els.deleteConfirm.value = '';
  syncDeleteButton();
}

function initProfile() {
  cache();
  els.openBtn?.addEventListener('click', openProfile);
  els.botLabBtn?.addEventListener('click', openProfile);
  els.closeBtn?.addEventListener('click', closeProfile);
  els.modal?.addEventListener('click', (event) => {
    if (event.target === els.modal) closeProfile();
  });
  els.uploadBtn?.addEventListener('click', () => {
    if (busy) return;
    els.fileInput?.click();
  });
  els.fileInput?.addEventListener('change', () => {
    const file = els.fileInput.files?.[0] || null;
    // Cleared before the upload resolves so picking the same file twice (after
    // fixing it on disk) still fires a change event.
    els.fileInput.value = '';
    void uploadFile(file);
  });
  els.removeBtn?.addEventListener('click', () => {
    if (busy) return;
    void removeModel();
  });
  els.deleteConfirm?.addEventListener('input', syncDeleteButton);
  els.deleteBtn?.addEventListener('click', () => {
    // disabled is the real gate, so re-read it rather than re-deriving the
    // match here -- two copies of that rule could disagree.
    if (busy || els.deleteBtn.disabled) return;
    void deleteAccount();
  });
}

export { initProfile, openProfile, closeProfile };
